import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    MailWarning, Loader2, Search, X, CheckCircle2, Trash2, Clock, Eye,
    Paperclip, ShieldCheck, ShieldOff, Send, RefreshCw, Palette, Sparkles, Merge, Pencil,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { useGearsVisible } from "../lib/gearVisibility";

const STATUS_META = {
    pending: { label: "Wartet auf Freigabe", cls: "bg-amber-100 text-amber-900 border-amber-300", Icon: Clock },
    approved: { label: "Freigegeben", cls: "bg-emerald-100 text-emerald-900 border-emerald-300", Icon: CheckCircle2 },
    discarded: { label: "Verworfen", cls: "bg-gray-100 text-gray-600 border-gray-300", Icon: Trash2 },
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("de-DE", {
            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
};

function StatusBadge({ status, testid }) {
    const m = STATUS_META[status] || { label: status, cls: "bg-gray-100 text-gray-700 border-gray-300", Icon: Clock };
    const { Icon } = m;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm border ${m.cls}`} data-testid={testid}>
            <Icon className="w-3 h-3" /> {m.label}
        </span>
    );
}

export default function EmailOutboxPage() {
    const gearsOk = useGearsVisible();

    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState({ items: [], total: 0, counts: {} });
    const [busy, setBusy] = useState(true);
    const [statusFilter, setStatusFilter] = useState("pending");
    const [sourceFilter, setSourceFilter] = useState("all");
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);
    const [detailBusy, setDetailBusy] = useState(false);
    const [acting, setActing] = useState(false);
    const [settings, setSettings] = useState(null);
    // Jul-2026 (User-Wunsch) — Vorlagen-Automatik: Map key → {label, auto_send}
    const [templates, setTemplates] = useState({});

    const loadTemplates = useCallback(() => {
        api.get("/dealer/email-templates?area=all").then(r => {
            const map = {};
            (r.data?.items || []).forEach(t => { map[t.key] = { label: t.label, auto_send: !!t.auto_send }; });
            setTemplates(map);
        }).catch(() => {});
    }, []);

    const toggleAutoSend = async (key) => {
        const cur = templates[key];
        if (!cur) return;
        const next = !cur.auto_send;
        if (next && !window.confirm(`Automatik für „${cur.label}" aktivieren?\n\nKünftige Mails dieses Typs gehen OHNE Freigabe raus — und alle aktuell WARTENDEN Mails dieses Typs werden SOFORT versendet.`)) return;
        setTemplates(m => ({ ...m, [key]: { ...cur, auto_send: next } }));
        try {
            const r = await api.put(`/dealer/email-templates/${key}/auto-send`, { auto_send: next });
            toast.success(next
                ? `„${cur.label}" wird künftig AUTOMATISCH gesendet (ohne Freigabe).${r.data?.sent_now ? ` ${r.data.sent_now} wartende Mail(s) sofort versendet.` : ""}`
                : `„${cur.label}" wartet künftig wieder in der Freigabe.`);
            if (next && r.data?.sent_now) load();
        } catch (e) {
            setTemplates(m => ({ ...m, [key]: cur }));
            toast.error(e?.response?.data?.detail || "Umschalten fehlgeschlagen");
        }
    };

    const canAccess = user && (
        user.role === "b2b" || user.role === "admin" ||
        (user.role === "zweiradverkaeufer" && user.employee_function === "filialleiter")
    );

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const params = { status: statusFilter };
            if (q.trim()) params.q = q.trim();
            if (sourceFilter && sourceFilter !== "all") params.source = sourceFilter;
            const r = await api.get("/dealer/email-outbox", { params });
            setData(r.data);
        } catch (e) { console.error(e); }
        finally { setBusy(false); }
    }, [statusFilter, q, sourceFilter]);

    // Bereich-Link je Vorlage: Leasing-Texte werden unter „Leasing" gepflegt.
    const editTemplateLink = (key) => key && key.startsWith("leasing_")
        ? `/dealer/leasingrueckgabe?settings=texte&focus=${key}`
        : `/dashboard?tab=settings&template=${key}#settings-email-templates`;

    const SOURCE_LABELS = {
        all: "Alle Bereiche", leasing: "Leasing", finanzierung: "Finanzierung",
        werkstatt: "Werkstatt", rechnung: "Rechnung", allgemein: "Allgemein",
    };
    const SOURCE_BADGE = {
        leasing: "bg-orange-100 text-orange-700 border border-orange-200",
        finanzierung: "bg-blue-100 text-blue-700 border border-blue-200",
        werkstatt: "bg-green-100 text-green-700 border border-green-200",
        rechnung: "bg-purple-100 text-purple-700 border border-purple-200",
        allgemein: "bg-gray-100 text-gray-600 border border-gray-200",
    };

    useEffect(() => {
        if (loading) return;
        if (!canAccess) { navigate("/"); return; }
        load();
        loadTemplates();
        api.get("/dealer/email-outbox-settings").then(r => setSettings(r.data)).catch(() => {});
    }, [loading, canAccess, navigate, load, loadTemplates]);

    const openDetail = async (item) => {
        setSelected(item);
        setDetailBusy(true);
        try {
            const r = await api.get(`/dealer/email-outbox/${item.id}`);
            setSelected(r.data);
        } catch (e) { console.error(e); }
        finally { setDetailBusy(false); }
    };

    const approve = async (item) => {
        setActing(true);
        try {
            const r = await api.post(`/dealer/email-outbox/${item.id}/approve`);
            toast.success(r.data.sent_status === "sent"
                ? "E-Mail freigegeben & versendet"
                : "E-Mail freigegeben (Versand simuliert — noch kein E-Mail-Key hinterlegt)");
            setSelected(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Freigabe fehlgeschlagen");
        } finally { setActing(false); }
    };

    const discard = async (item) => {
        setActing(true);
        try {
            await api.post(`/dealer/email-outbox/${item.id}/discard`);
            toast.success("E-Mail verworfen — wird nicht versendet");
            setSelected(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Verwerfen fehlgeschlagen");
        } finally { setActing(false); }
    };

    // Jul-2026 (User-Wunsch) — Löschen & Neu-Generieren in der Freigabe-Liste
    const removeItem = async (item) => {
        setActing(true);
        try {
            await api.delete(`/dealer/email-outbox/${item.id}`);
            toast.success("E-Mail gelöscht");
            setSelected(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
        } finally { setActing(false); }
    };

    const regenerate = async (item) => {
        setActing(true);
        try {
            const r = await api.post(`/dealer/email-outbox/${item.id}/regenerate`);
            toast.success(r.data.branded
                ? "E-Mail mit aktuellem Firmenbranding neu generiert"
                : "E-Mail neu generiert");
            const d = await api.get(`/dealer/email-outbox/${item.id}`);
            setSelected(d.data);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Neu-Generieren fehlgeschlagen");
        } finally { setActing(false); }
    };

    // Jul-2026 (User-Wunsch) — Text direkt per KI bearbeiten (+ optional in Vorlage speichern)
    const [aiOpen, setAiOpen] = useState(false);
    const [aiInstruction, setAiInstruction] = useState("");
    const [aiSaveTemplate, setAiSaveTemplate] = useState(false);
    const [aiBusy, setAiBusy] = useState(false);
    const aiEdit = async () => {
        if (!aiInstruction.trim() || !selected) return;
        setAiBusy(true);
        try {
            const r = await api.post(`/dealer/email-outbox/${selected.id}/ai-edit`, {
                instruction: aiInstruction.trim(),
                save_to_template: aiSaveTemplate,
            });
            toast.success(r.data.template_saved
                ? "Text per KI überarbeitet & in der Vorlage gespeichert"
                : "Text per KI überarbeitet");
            setAiInstruction("");
            setAiOpen(false);
            const d = await api.get(`/dealer/email-outbox/${selected.id}`);
            setSelected(d.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "KI-Bearbeitung fehlgeschlagen");
        } finally { setAiBusy(false); }
    };

    // Aug-2026 (User-Wunsch) — Alt-/Leasing-Mails mit veraltetem Text manuell bearbeiten
    const [editOpen, setEditOpen] = useState(false);
    const [editSubject, setEditSubject] = useState("");
    const [editBody, setEditBody] = useState("");
    const openEdit = () => {
        setEditSubject(selected?.subject || "");
        // Sichtbaren Fließtext aus dem HTML extrahieren (grobe Bereinigung)
        const raw = (selected?.body_html || "").replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "").replace(/\n{3,}/g, "\n\n").trim();
        setEditBody(raw);
        setAiOpen(false);
        setEditOpen(true);
    };
    const saveEdit = async () => {
        if (!selected) return;
        setActing(true);
        try {
            await api.patch(`/dealer/email-outbox/${selected.id}`, { subject: editSubject, body_html: editBody });
            toast.success("E-Mail-Text aktualisiert");
            setEditOpen(false);
            const d = await api.get(`/dealer/email-outbox/${selected.id}`);
            setSelected(d.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally { setActing(false); }
    };

    const toggleApproval = async (next) => {        try {
            await api.put("/dealer/email-outbox-settings", { approval_required: next });
            setSettings(s => ({ ...s, approval_required: next }));
            toast.success(next
                ? "Freigabepflicht aktiviert — keine E-Mail geht ohne Ihre Freigabe raus"
                : "Freigabepflicht deaktiviert — E-Mails werden ab sofort direkt versendet");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Änderung fehlgeschlagen");
        }
    };

    // Aug-2026 (User-Wunsch „Produktions-Aufräumer") — Alt-Duplikate bei den
    // E-Mail-Leads per 1 Klick zusammenführen (gleicher Interessent = 1 Lead).
    const [cleaning, setCleaning] = useState(false);
    const cleanupDuplicates = async () => {
        if (!window.confirm("Doppelte Lead-Anfragen jetzt bereinigen?\n\nGleicher Interessent (E-Mail / Telefon / Name) = 1 Lead. Weitere Anfragen werden am bestehenden Lead vermerkt.")) return;
        setCleaning(true);
        try {
            const r = await api.post("/dealer/email-outbox/cleanup-duplicates");
            toast.success(r.data.message || `${r.data.merged} Duplikat(e) bereinigt`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bereinigung fehlgeschlagen");
        } finally { setCleaning(false); }
    };

    const counts = data.counts || {};

    const approveAll = async () => {
        const n = counts.pending ?? 0;
        if (!window.confirm(`Alle ${n} wartenden E-Mails freigeben & senden?`)) return;
        setActing(true);
        try {
            const r = await api.post("/dealer/email-outbox/approve-all");
            if (r.data.hint) toast.error(r.data.hint, { duration: 9000 });
            toast.success(`${r.data.approved} E-Mail(s) freigegeben${r.data.failed ? ` · ${r.data.failed} fehlgeschlagen` : ""}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sammel-Freigabe fehlgeschlagen");
        } finally { setActing(false); }
    };

    return (
        <div className="max-w-6xl mx-auto px-4 py-8" data-testid="email-outbox-page">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-black tracking-tight flex items-center gap-2">
                        <MailWarning className="w-6 h-6" /> E-Mail-Freigabe
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Ausgehende E-Mails an Kunden warten hier auf Ihre Freigabe, bevor sie versendet werden.
                    </p>
                </div>
                {settings && (
                    <div className="flex items-center gap-3 border rounded-md px-4 py-2.5 bg-white" data-testid="outbox-settings-box">
                        {settings.approval_required
                            ? <ShieldCheck className="w-5 h-5 text-emerald-600" />
                            : <ShieldOff className="w-5 h-5 text-red-500" />}
                        <div className="text-xs">
                            <div className="font-bold">Freigabepflicht {settings.approval_required ? "aktiv" : "deaktiviert"}</div>
                            <div className="text-gray-500">{settings.approval_required
                                ? "Keine E-Mail geht ohne Freigabe raus"
                                : "E-Mails werden direkt versendet"}</div>
                        </div>
                        {settings.can_toggle && (
                            <Switch checked={settings.approval_required}
                                onCheckedChange={toggleApproval}
                                data-testid="outbox-approval-toggle" />
                        )}
                    </div>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4">
                {["pending", "approved", "discarded"].map(s => (
                    <button key={s} onClick={() => setStatusFilter(s)}
                        data-testid={`outbox-filter-${s}`}
                        className={`text-xs font-bold px-3 py-1.5 rounded-sm border transition-colors ${statusFilter === s ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-300 hover:border-gray-500"}`}>
                        {STATUS_META[s].label} ({counts[s] ?? 0})
                    </button>
                ))}
                <div className="relative ml-auto flex items-center gap-2">
                    <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
                        data-testid="outbox-source-filter"
                        title="Nach Bereich filtern, für den die E-Mails erzeugt wurden"
                        className="text-xs font-bold px-2 py-2 rounded-sm border border-gray-300 bg-white text-gray-700">
                        <option value="all">Alle Bereiche{data.sources ? ` (${Object.values(data.sources).reduce((a, b) => a + b, 0)})` : ""}</option>
                        {Object.entries(data.sources || {}).sort().map(([k, n]) => (
                            <option key={k} value={k}>{(SOURCE_LABELS[k] || k)} ({n})</option>
                        ))}
                    </select>
                    <button onClick={cleanupDuplicates} disabled={cleaning}
                        data-testid="outbox-cleanup-duplicates-btn"
                        title="Doppelte Lead-Anfragen zusammenführen: gleicher Interessent (E-Mail / Telefon / Name) = 1 Lead"
                        className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-sm border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-50">
                        {cleaning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Merge className="w-3.5 h-3.5" />}
                        Duplikate bereinigen
                    </button>
                    {statusFilter === "pending" && (counts.pending ?? 0) > 0 && (
                        <button onClick={approveAll} disabled={acting}
                            data-testid="outbox-approve-all-btn"
                            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-sm bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50">
                            {acting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                            Alle freigeben ({counts.pending})
                        </button>
                    )}
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
                        <input value={q} onChange={e => setQ(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && load()}
                            placeholder="Empfänger / Betreff suchen…"
                            data-testid="outbox-search-input"
                            className="pl-8 pr-3 py-2 text-sm border rounded-md w-64" />
                    </div>
                </div>
            </div>

            {busy ? (
                <div className="flex items-center justify-center py-16 text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin" />
                </div>
            ) : data.items.length === 0 ? (
                <div className="border border-dashed rounded-md py-16 text-center text-sm text-gray-400" data-testid="outbox-empty">
                    {statusFilter === "pending" ? "Keine E-Mails warten auf Freigabe." : "Keine Einträge."}
                </div>
            ) : (
                <div className="border rounded-md divide-y bg-white" data-testid="outbox-list">
                    {data.items.map(item => (
                        <div key={item.id} role="button" tabIndex={0} onClick={() => openDetail(item)}
                            data-testid={`outbox-item-${item.id}`}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 flex flex-wrap items-center gap-x-4 gap-y-1 cursor-pointer">
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-bold truncate flex items-center gap-2">
                                    <span className={`shrink-0 text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-sm ${SOURCE_BADGE[item.source] || SOURCE_BADGE.allgemein}`}
                                        data-testid={`outbox-source-badge-${item.id}`}>
                                        {SOURCE_LABELS[item.source] || SOURCE_LABELS.allgemein}
                                    </span>
                                    <span className="truncate">{item.subject || "(ohne Betreff)"}</span>
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                    an {item.to}{item.branch_site_code ? ` · Standort ${item.branch_site_code}` : ""}
                                    {item.triggered_by_name ? ` · ausgelöst von ${item.triggered_by_name}` : ""}
                                </div>
                                {item.vehicle_ctx && (
                                    <div className="text-xs text-gray-600 truncate" data-testid={`outbox-vehicle-${item.id}`}>
                                        {item.vehicle_ctx.vehicle_number && <span className="font-mono-num font-bold">{item.vehicle_ctx.vehicle_number}</span>}
                                        {item.vehicle_ctx.seller_name && <span>{item.vehicle_ctx.vehicle_number ? " · " : ""}Verkäufer: {item.vehicle_ctx.seller_name}</span>}
                                        {item.vehicle_ctx.sale_date && <span> · verkauft am {new Date(item.vehicle_ctx.sale_date).toLocaleDateString("de-DE")}</span>}
                                    </div>
                                )}
                                {/* Jul-2026 (User-Wunsch) — Vorlagen-Automatik direkt an jeder E-Mail */}
                                {item.template_key && templates[item.template_key] && (
                                    <div className="mt-1 flex items-center gap-1.5 flex-wrap" onClick={e => e.stopPropagation()}>
                                        <span className="text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-sm truncate max-w-[220px]"
                                            title={`Vorlage: ${templates[item.template_key].label}`}>
                                            {item.template_label || templates[item.template_key].label}
                                        </span>
                                        <button onClick={() => navigate(editTemplateLink(item.template_key))}
                                            data-testid={`outbox-edit-template-${item.id}`}
                                            title="Diese Vorlage in den Einstellungen bearbeiten"
                                            className="text-[10px] font-bold text-[#111827] underline decoration-[#FFCC00] decoration-2 hover:text-[#B8860B]">
                                            Vorlage bearbeiten
                                        </button>
                                        <button onClick={() => toggleAutoSend(item.template_key)}
                                            data-testid={`outbox-toggle-auto-${item.id}`}
                                            title={templates[item.template_key].auto_send
                                                ? "Automatik AN — künftige Mails dieses Typs gehen OHNE Freigabe raus. Klicken für Freigabepflicht."
                                                : "Freigabepflicht — künftige Mails dieses Typs automatisch senden? Klicken zum Aktivieren."}
                                            className={`text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-sm border transition-colors ${templates[item.template_key].auto_send ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-gray-300 text-gray-500 hover:border-emerald-400"}`}>
                                            {templates[item.template_key].auto_send ? "Automatik AN" : "Auto senden: AUS"}
                                        </button>
                                    </div>
                                )}
                            </div>
                            {item.status === "pending" && (
                                <span className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                                    <button onClick={() => approve(item)} disabled={acting}
                                        data-testid={`outbox-row-approve-${item.id}`}
                                        title="Diese E-Mail einzeln freigeben & senden"
                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50">
                                        <Send className="w-3 h-3" /> Freigeben
                                    </button>
                                    <button onClick={() => discard(item)} disabled={acting}
                                        data-testid={`outbox-row-discard-${item.id}`}
                                        title='Verwerfen — wird NICHT versendet (endgültig löschen dann im Tab Verworfen)'
                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50">
                                        <Trash2 className="w-3 h-3" /> Verwerfen
                                    </button>
                                </span>
                            )}
                            {item.status === "discarded" && (
                                <button onClick={e => { e.stopPropagation(); removeItem(item); }} disabled={acting}
                                    data-testid={`outbox-row-delete-${item.id}`}
                                    title="Endgültig löschen"
                                    className="inline-flex items-center justify-center w-7 h-7 rounded-sm border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            )}
                            {item.status !== "pending" && <StatusBadge status={item.status} testid={`outbox-status-${item.id}`} />}
                            <span className="text-xs text-gray-400 w-32 text-right">{fmtDate(item.created_at)}</span>
                        </div>
                    ))}
                </div>
            )}

            {selected && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-md w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="outbox-detail-modal">
                        <div className="flex items-start justify-between gap-4 p-4 border-b">
                            <div className="min-w-0">
                                <div className="font-bold truncate">{selected.subject || "(ohne Betreff)"}</div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                    an {selected.to} · {fmtDate(selected.created_at)}
                                    {(selected.cc || []).length > 0 && ` · CC: ${selected.cc.join(", ")}`}
                                </div>
                                {(selected.attachments || []).length > 0 && (
                                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-1 flex-wrap">
                                        <Paperclip className="w-3 h-3" />
                                        {selected.attachments.map((a, i) => (
                                            <span key={i} className="bg-gray-100 rounded px-1.5 py-0.5">{a.filename} ({a.size_kb} KB)</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button onClick={() => setSelected(null)} data-testid="outbox-detail-close" className="text-gray-400 hover:text-gray-700">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto p-2 bg-gray-50">
                            {detailBusy ? (
                                <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
                            ) : (
                                <>
                                <div className="flex items-center gap-1.5 px-1 pb-1.5 text-[11px] text-gray-500" data-testid="outbox-preview-hint">
                                    <Eye className="w-3.5 h-3.5 text-emerald-600" />
                                    Vorschau im finalen Kunden-Layout — exakt so wird die E-Mail versendet (inkl. Firmen-Branding).
                                </div>
                                <iframe title="E-Mail-Vorschau" srcDoc={selected.body_html || ""}
                                    sandbox="" className="w-full h-[50vh] bg-white border rounded" data-testid="outbox-preview-iframe" />
                                </>
                            )}
                        </div>
                        {selected.status === "pending" && (
                            <>
                            {aiOpen && (
                                <div className="px-4 pt-3 border-t bg-violet-50/60" data-testid="outbox-ai-edit-panel">
                                    <div className="text-xs font-bold text-gray-700 mb-1 flex items-center gap-1.5">
                                        <Sparkles className="w-3.5 h-3.5 text-violet-600" /> Text mit KI bearbeiten
                                    </div>
                                    <textarea value={aiInstruction} onChange={e => setAiInstruction(e.target.value)}
                                        rows={2} placeholder='z. B. "Formuliere freundlicher und kürzer" oder "Entferne den Standgeld-Hinweis"'
                                        data-testid="outbox-ai-instruction-input"
                                        className="w-full text-sm border rounded-md px-3 py-2 bg-white" />
                                    <div className="flex items-center justify-between gap-2 pb-3 pt-1 flex-wrap">
                                        {selected.template_key ? (
                                            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                                                <input type="checkbox" checked={aiSaveTemplate}
                                                    onChange={e => setAiSaveTemplate(e.target.checked)}
                                                    data-testid="outbox-ai-save-template-checkbox" />
                                                Auch dauerhaft in der Vorlage speichern (Einstellungen)
                                            </label>
                                        ) : <span className="text-xs text-gray-400">Keine Vorlage verknüpft — Änderung gilt nur für diese E-Mail</span>}
                                        <div className="flex items-center gap-2">
                                            <button onClick={() => setAiOpen(false)}
                                                className="text-xs font-bold px-3 py-1.5 rounded-sm border border-gray-300 text-gray-600 hover:border-gray-500">
                                                Abbrechen
                                            </button>
                                            <button onClick={aiEdit} disabled={aiBusy || !aiInstruction.trim()}
                                                data-testid="outbox-ai-edit-submit"
                                                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">
                                                {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                                                Überarbeiten
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {editOpen && (
                                <div className="px-4 pt-3 border-t bg-amber-50/60" data-testid="outbox-edit-panel">
                                    <div className="text-xs font-bold text-gray-700 mb-1">Text manuell bearbeiten</div>
                                    <input value={editSubject} onChange={e => setEditSubject(e.target.value)}
                                        placeholder="Betreff" data-testid="outbox-edit-subject-input"
                                        className="w-full text-sm border rounded-md px-3 py-2 bg-white mb-2" />
                                    <textarea value={editBody} onChange={e => setEditBody(e.target.value)}
                                        rows={6} placeholder="E-Mail-Text…" data-testid="outbox-edit-body-input"
                                        className="w-full text-sm border rounded-md px-3 py-2 bg-white" />
                                    <div className="flex items-center justify-end gap-2 py-2">
                                        <button onClick={() => setEditOpen(false)}
                                            className="text-xs font-bold px-3 py-1.5 rounded-sm border border-gray-300 text-gray-600 hover:border-gray-500">
                                            Abbrechen
                                        </button>
                                        <button onClick={saveEdit} disabled={acting}
                                            data-testid="outbox-edit-save"
                                            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50">
                                            {acting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Speichern
                                        </button>
                                    </div>
                                </div>
                            )}
                            <div className="flex items-center justify-end gap-2 p-4 border-t flex-wrap">
                                <button onClick={openEdit} disabled={acting}
                                    data-testid="outbox-edit-btn"
                                    title="Betreff/Text dieser E-Mail manuell bearbeiten"
                                    className="inline-flex items-center gap-1.5 text-sm font-bold px-3 py-2 rounded-sm border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50">
                                    <Pencil className="w-4 h-4" /> Text bearbeiten
                                </button>
                                {gearsOk && <button onClick={() => navigate("/dashboard?tab=settings#settings-layout-branding")}
                                    data-testid="outbox-edit-settings-btn"
                                    title="E-Mail-Vorlagen & Markendesign in den Einstellungen bearbeiten"
                                    className="inline-flex items-center gap-1.5 text-sm font-bold px-3 py-2 rounded-sm border border-gray-300 text-gray-700 hover:border-gray-500 mr-auto">
                                    <Palette className="w-4 h-4" /> Design bearbeiten
                                </button>}
                                {selected.template_key && gearsOk && (
                                    <button onClick={() => navigate(editTemplateLink(selected.template_key))}
                                        data-testid="outbox-detail-edit-template-btn"
                                        title="Die Vorlage dieser E-Mail in den Einstellungen bearbeiten"
                                        className="inline-flex items-center gap-1.5 text-sm font-bold px-3 py-2 rounded-sm border border-gray-300 text-gray-700 hover:border-gray-500">
                                        Vorlage bearbeiten
                                    </button>
                                )}
                                <button onClick={() => setAiOpen(v => !v)} disabled={aiBusy}
                                    data-testid="outbox-ai-edit-btn"
                                    title="Text dieser E-Mail per KI umschreiben lassen"
                                    className="inline-flex items-center gap-1.5 text-sm font-bold px-3 py-2 rounded-sm border border-violet-300 text-violet-700 hover:bg-violet-50 disabled:opacity-50">
                                    <Sparkles className="w-4 h-4" /> Mit KI bearbeiten
                                </button>
                                <button onClick={() => regenerate(selected)} disabled={acting}
                                    data-testid="outbox-regenerate-btn"
                                    title="E-Mail mit dem aktuellen Firmenbranding neu generieren"
                                    className="inline-flex items-center gap-1.5 text-sm font-bold px-3 py-2 rounded-sm border border-gray-300 text-gray-700 hover:border-gray-500 disabled:opacity-50">
                                    <RefreshCw className={`w-4 h-4 ${acting ? "animate-spin" : ""}`} /> Neu generieren
                                </button>
                                <button onClick={() => discard(selected)} disabled={acting}
                                    data-testid="outbox-discard-btn"
                                    title='Verwerfen — wird NICHT versendet (endgültig löschen dann im Tab Verworfen)'
                                    className="inline-flex items-center gap-1.5 text-sm font-bold px-3 py-2 rounded-sm border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50">
                                    <Trash2 className="w-4 h-4" /> Verwerfen
                                </button>
                                <button onClick={() => approve(selected)} disabled={acting}
                                    data-testid="outbox-approve-btn"
                                    className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-sm bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50">
                                    {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                    Freigeben &amp; senden
                                </button>
                            </div>
                            </>
                        )}
                        {selected.status === "approved" && (
                            <div className="p-4 border-t text-xs text-gray-500">
                                Freigegeben von {selected.approved_by_name || "—"} am {fmtDate(selected.approved_at)}
                                {selected.sent_status === "mock" && " · Versand simuliert (kein E-Mail-Key)"}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
